/**
 * services/user.ts
 * React Query hooks for the User domain.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost } from '@/lib/apiClient'
import { env } from '@/config/env'
import type {
  AppUser,
  UserRoleAssignment,
  StaffBadge,
  UserRoleOption,
  AddUserPayload,
  EditUserPayload,
  DUStaffRegistration,
  ProfileDetailsByEmail,
  UpsertDUStaffRegistrationPayload,
} from '@/types/user'

export const userKeys = {
  all:      () => ['users'] as const,
  list:     (tenant?: string) => ['users', 'list', tenant ?? ''] as const,
  byEmail:  (email: string)   => ['users', 'byEmail', email] as const,
  detail:   (userId: string)  => ['users', 'detail', userId] as const,
  badges:   (userId: string)  => ['users', 'badges', userId] as const,
  roles:    () => ['users', 'roles'] as const,
  duStaff:  () => ['users', 'du-staff'] as const,
  profileByEmail: (email: string) => ['users', 'profile-by-email', email] as const,
}

/**
 * Builds the Profiles API photo URL for a given email.
 * Matches Blazor ApplicationConfigs.ProfilesAPI pattern:
 *   VITE_PROFILES_API_URL is the full template with {email} as placeholder.
 *   Example: https://sdu.deloitte.com/.../profilepicture?size=standard&apikey=xxx&search={email}
 * Returns null when the env var is not configured (falls back to initials).
 */
export function getProfilePictureUrl(email: string): string | null {
  const template = env.VITE_PROFILES_API_URL
  if (!template || !email) return null

  const encodedEmail = encodeURIComponent(email.trim())

  // Support both React placeholder ({email}) and legacy Blazor placeholder ({0}).
  if (template.includes('{email}')) {
    return template.replace('{email}', encodedEmail)
  }
  if (template.includes('{0}')) {
    return template.replace('{0}', encodedEmail)
  }

  // Fallback: append search query when no placeholder exists.
  const separator = template.includes('?') ? '&' : '?'
  return `${template}${separator}search=${encodedEmail}`
}

/** GET User/GetUserByEmail?Email={email} — used by useBootstrapUser on login */
export function useUserByEmail(email: string) {
  return useQuery({
    queryKey: userKeys.byEmail(email),
    queryFn:  () => apiGet<AppUser>(`User/GetUserByEmail?Email=${encodeURIComponent(email)}`),
    enabled:  !!email,
    staleTime: 5 * 60 * 1000, // 5 min — user data doesn't change mid-session
    retry: 1,
  })
}

export function useUsers(tenant?: string) {
  return useQuery({
    queryKey: userKeys.list(tenant),
    queryFn:  () => apiGet<AppUser[]>(`User/GetUsers${tenant ? `?tenant=${encodeURIComponent(tenant)}` : ''}`),
  })
}

export function useRoles() {
  return useQuery({
    queryKey: userKeys.roles(),
    queryFn: () => apiGet<UserRoleOption[]>('User/GetRoles'),
  })
}

export function useDUStaffRegistrations() {
  return useQuery({
    queryKey: userKeys.duStaff(),
    queryFn: () => apiGet<DUStaffRegistration[]>('Registrations/GetDUStaffRegistrations'),
  })
}

export function getProfileDetailsByEmail(email: string) {
  return apiGet<ProfileDetailsByEmail>(`Registrations/GetProfileDetailsByEmail?email=${encodeURIComponent(email)}`)
}

export function useUser(userId: string) {
  return useQuery({
    queryKey: userKeys.detail(userId),
    queryFn:  () => apiGet<AppUser>(`Users/${encodeURIComponent(userId)}`),
    enabled:  !!userId,
  })
}

export function useStaffBadges(userId: string) {
  return useQuery({
    queryKey: userKeys.badges(userId),
    queryFn:  () => apiGet<StaffBadge[]>(`Registrations/GetDUStaffRegistrations`),
    enabled:  !!userId,
  })
}

export function useAssignRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: Omit<UserRoleAssignment, 'assignedAt' | 'assignedBy'>) =>
      apiPost<UserRoleAssignment>('User/AddUser', payload),
    onSuccess: (_, variables) => {
      void qc.invalidateQueries({ queryKey: userKeys.detail(variables.userId) })
      void qc.invalidateQueries({ queryKey: userKeys.list() })
    },
  })
}

export function useIssueStaffBadge() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: Pick<StaffBadge, 'userId' | 'badgeType'>) =>
      apiPost<StaffBadge>('Registration/UpsertDUStaffRegistration', payload),
    onSuccess: (_, variables) => {
      void qc.invalidateQueries({ queryKey: userKeys.badges(variables.userId) })
    },
  })
}

export function useUpsertDUStaffRegistration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpsertDUStaffRegistrationPayload) =>
      apiPost<string>('Registration/UpsertDUStaffRegistration', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: userKeys.duStaff() })
    },
  })
}

export function useUpdateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: Partial<AppUser> & { userId: string }) =>
      apiPost<AppUser>('User/EditUser', payload),
    onSuccess: (_, variables) => {
      void qc.invalidateQueries({ queryKey: userKeys.list() })
      void qc.invalidateQueries({ queryKey: userKeys.byEmail(variables.userId) })
    },
  })
}

export function useAddManagedUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: AddUserPayload) => apiPost<string>('User/AddUser', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: userKeys.list() })
    },
  })
}

export function useEditManagedUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: EditUserPayload) => apiPost<string>('User/EditUser', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: userKeys.list() })
    },
  })
}
